import "./sell.page.scss";
import { Shell } from "../../common/shell/shell.page";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Sell, SellPaymentMethod } from "../sell.entity";
import EntityListObservable from "../../common/entity-list-observable";
import { v4 } from "uuid";
import { sellRepository } from "../sell.repository";
import { useLocalStorageState } from "../../common/hooks/localstorage-state";
import { SellComponent } from "../components/sell-component";
import Moment from "react-moment";
import moment from "moment";
import "moment/locale/pt-br";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useCashRegister } from "../../cash-register/cash-register-context";
import { LoadingOverlay } from "../../common/components/loading-overlay";

const formatBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const SellPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  // Bug fix: was useState(true), leaving the button permanently disabled on first load
  const [loading, setLoading] = useState(false);

  const [sellId, setSellId] = useLocalStorageState<string>("sell_id");
  const [unpaidSells] = sellRepository.useAllUnpaidState();
  const {
    featureEnabled: cashFlowOn,
    loading: cashLoading,
    session: cashSession,
    sessionExpired,
    canManage: canManageCash,
  } = useCashRegister();

  application.useHasPermission(Permission.SELL);

  const cashSessionId =
    cashFlowOn && cashSession && !sessionExpired ? cashSession.id : null;

  const filteredUnpaid = useMemo(() => {
    const withCustomer = unpaidSells.filter((s) => s.getCustomer() !== "");
    if (!cashFlowOn || !cashSession || sessionExpired) return [];
    return withCustomer.filter((s) => {
      const cid = s.getCashSessionId();
      return cid === null || cid === cashSession.id;
    });
  }, [unpaidSells, cashFlowOn, cashSession, sessionExpired]);

  const registerSell = useCallback(async () => {
    if (cashFlowOn && (!cashSession || sessionExpired)) return;
    setLoading(true);
    const sellerUserId = application.user.get()?.id ?? null;
    const newSell = new Sell(
      v4(),
      new EntityListObservable(),
      SellPaymentMethod.UNPAID,
      "",
      moment(),
      null,
      sellerUserId,
      cashSessionId
    );
    await sellRepository.save(newSell);
    setSellId(newSell.id);
    setLoading(false);
  }, [setSellId, cashFlowOn, cashSession, sessionExpired, cashSessionId]);

  useEffect(() => {
    const keyEvent = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        if (sellId) return;
        if (cashFlowOn && (!cashSession || sessionExpired)) return;
        registerSell();
      }
    };
    document.addEventListener("keydown", keyEvent);
    return () => document.removeEventListener("keydown", keyEvent);
  }, [registerSell, sellId, cashFlowOn, cashSession, sessionExpired]);

  useEffect(() => {
    if (cashFlowOn && sessionExpired && sellId) {
      setSellId(null);
    }
  }, [cashFlowOn, sessionExpired, sellId, setSellId]);

  if (!sellId) {
    if (cashFlowOn && cashLoading) {
      return (
        <Shell title="Vender">
          <LoadingOverlay message="Verificando caixa…" />
        </Shell>
      );
    }

    if (cashFlowOn && sessionExpired) {
      return (
        <Shell title="Vender">
          <LoadingOverlay message="Redirecionando para fechamento do caixa…" />
        </Shell>
      );
    }

    if (cashFlowOn && !cashSession) {
      return (
        <Shell title="Vender">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              border: 1,
              borderColor: "warning.main",
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.07)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "warning.main",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
                  Caixa fechado
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {canManageCash
                    ? "Abra o caixa em Gestão de caixa para começar a vender."
                    : "Peça a alguém com permissão de gestão de caixa para abrir o caixa."}
                </Typography>
              </Box>
              {canManageCash && (
                <Button variant="contained" color="warning" onClick={() => navigate("/caixa")}>
                  Abrir caixa
                </Button>
              )}
            </Stack>
          </Paper>

          <Alert severity="warning">
            Com o fluxo de caixa ativo é necessário abrir o caixa antes de iniciar vendas.
          </Alert>
        </Shell>
      );
    }

    const sortedUnpaid = [...filteredUnpaid].sort(
      (a, b) => b.getStartedAt().valueOf() - a.getStartedAt().valueOf()
    );

    return (
      <Shell title="Vender">
        <Stack spacing={3}>
          {/* Start card */}
          <Paper
            elevation={0}
            onClick={loading ? undefined : registerSell}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              border: 2,
              borderColor: loading ? "divider" : "primary.main",
              borderRadius: 3,
              cursor: loading ? "default" : "pointer",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.dark, 0.02)} 100%)`,
              transition: "box-shadow 0.15s, border-color 0.15s",
              "&:hover": loading ? {} : { boxShadow: theme.shadows[4] },
              userSelect: "none",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: loading
                    ? theme.palette.action.disabledBackground
                    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: loading ? "text.disabled" : "white",
                  flexShrink: 0,
                  transition: "background 0.15s",
                }}
              >
                <PointOfSaleOutlinedIcon sx={{ fontSize: 32 }} />
              </Box>

              <Box flex={1}>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  letterSpacing="-0.02em"
                  color={loading ? "text.disabled" : "text.primary"}
                >
                  Iniciar nova venda
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Clique aqui ou pressione{" "}
                  <Chip label="Enter" size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem", verticalAlign: "middle" }} />{" "}
                  para abrir o caixa
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowRoundedIcon />}
                disabled={loading}
                onClick={(e) => { e.stopPropagation(); registerSell(); }}
                sx={{ fontWeight: 700, flexShrink: 0 }}
              >
                {loading ? "Iniciando…" : "Iniciar venda"}
              </Button>
            </Stack>
          </Paper>

          {/* Open sells */}
          {sortedUnpaid.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={700}
                sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
              >
                <ReceiptLongIcon fontSize="small" />
                Vendas em aberto com cliente
                <Chip label={sortedUnpaid.length} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
              </Typography>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 700, display: { xs: "none", sm: "table-cell" } }}>
                        Aberta
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                      <TableCell sx={{ width: 90 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedUnpaid.map((sell) => (
                      <TableRow
                        key={sell.id}
                        onClick={() => setSellId(sell.id)}
                        sx={{
                          "&:last-child td": { borderBottom: 0 },
                          "&:hover": { bgcolor: "action.hover" },
                          cursor: "pointer",
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: "primary.main",
                                flexShrink: 0,
                              }}
                            >
                              <ReceiptLongIcon fontSize="small" />
                            </Box>
                            <Typography fontWeight={600}>
                              {sell.getCustomer() || "Cliente não informado"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                          <Typography variant="body2" color="text.secondary">
                            <Moment date={sell.getStartedAt()} fromNow locale="pt-br" />
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color="primary.main">
                            {formatBRL(sell.getSellTotal())}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); setSellId(sell.id); }}
                          >
                            Retomar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Stack>
      </Shell>
    );
  }

  if (cashFlowOn && sessionExpired) {
    return (
      <Shell title="Vender">
        <LoadingOverlay message="Redirecionando para fechamento do caixa…" />
      </Shell>
    );
  }

  return (
    <SellComponent
      sellId={sellId}
      cashSessionId={cashSessionId}
      closeSell={() => setSellId(null)}
    />
  );
};
